// Client-side push helpers. Kept out of App.jsx to keep that file focused.

const VAPID_PUB = import.meta.env.VITE_VAPID_PUBLIC_KEY;

const LS = {
  userId: 'tnf.userId',         // 'johnny' | 'elleke' | '_dismissed'
  pushEnabled: 'tnf.pushEnabled', // 'true' | 'false' | (unset)
  lastSync: 'tnf.pushLastSync',   // ms timestamp of last successful server resync
};
const SYNC_EVERY_MS = 60 * 60 * 1000; // re-check with the server at most hourly

export const ID = {
  get: () => localStorage.getItem(LS.userId),
  set: (v) => localStorage.setItem(LS.userId, v),
  isReal: (v) => v === 'johnny' || v === 'elleke',
};

export function pushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function permission() {
  if (!pushSupported()) return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

export function bannerShouldShow() {
  if (!pushSupported()) return false;
  if (Notification.permission !== 'default') return false;
  const id = ID.get();
  if (id === '_dismissed') return false;
  return !ID.isReal(id);
}

function urlBase64ToUint8Array(b64) {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

// Drop whatever subscription the browser holds and create a new one. A stale
// subscription can look fine locally while the push service has already
// invalidated it — a fresh one guarantees a live endpoint.
async function freshSubscription(reg) {
  const existing = await reg.pushManager.getSubscription();
  if (existing) { try { await existing.unsubscribe(); } catch {} }
  return reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(VAPID_PUB),
  });
}

async function register(userId, sub) {
  const res = await fetch('/.netlify/functions/subscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      userId,
      deviceLabel: navigator.userAgent.slice(0, 200),
      subscription: sub.toJSON(),
    }),
  });
  if (!res.ok) throw new Error(`subscribe-failed-${res.status}`);
  return res.json();
}

export async function enable(userId) {
  if (!pushSupported()) throw new Error('push-unsupported');
  if (!VAPID_PUB) throw new Error('vapid-key-missing');
  if (!ID.isReal(userId)) throw new Error('bad-user');

  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error(`permission-${perm}`);

  const reg = await navigator.serviceWorker.ready;
  const sub = await freshSubscription(reg);
  await register(userId, sub);

  ID.set(userId);
  localStorage.setItem(LS.pushEnabled, 'true');
  localStorage.setItem(LS.lastSync, String(Date.now()));
  return true;
}

// Run on app open / when the app comes back to the foreground. Makes sure the
// server still has this device; if the server dropped it (expired, pruned),
// re-subscribes. Returns 'ok' | 'renewed' | 'off' | 'revoked' | 'unsupported'.
export async function resync({ force = false } = {}) {
  if (!pushSupported() || !VAPID_PUB) return 'unsupported';
  if (localStorage.getItem(LS.pushEnabled) !== 'true') return 'off';
  const userId = ID.get();
  if (!ID.isReal(userId)) return 'off';
  if (Notification.permission !== 'granted') {
    // Permission was revoked in OS/browser settings — reflect that in the UI.
    localStorage.setItem(LS.pushEnabled, 'false');
    return 'revoked';
  }
  const last = Number(localStorage.getItem(LS.lastSync) || 0);
  if (!force && Date.now() - last < SYNC_EVERY_MS) return 'ok';

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  let renewed = false;
  if (!sub) { sub = await freshSubscription(reg); renewed = true; }
  const { known } = await register(userId, sub);
  if (!known && !renewed) {
    // Server had forgotten this endpoint → assume it's dead and swap in a fresh one.
    try {
      const fresh = await freshSubscription(reg);
      await register(userId, fresh);
      renewed = true;
    } catch (e) {
      // Some browsers only allow subscribe() from a tap; the old endpoint is
      // registered again in the meantime. Settings → Enable fixes it for sure.
      console.warn('push resubscribe failed', e);
    }
  }
  localStorage.setItem(LS.lastSync, String(Date.now()));
  return renewed ? 'renewed' : 'ok';
}

export async function sendTest() {
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) throw new Error('No subscription on this device — tap Enable.');
  const res = await fetch('/.netlify/functions/subscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ test: true, endpoint: sub.endpoint }),
  });
  const out = await res.json().catch(() => ({}));
  if (!out.ok) throw new Error(out.error === 'not-registered' ? 'Server doesn’t know this device — tap Enable.' : 'Test push failed.');
  return true;
}

export async function disable() {
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  const endpoint = sub?.endpoint;
  if (sub) await sub.unsubscribe();
  if (endpoint) {
    await fetch('/.netlify/functions/subscribe', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint }),
    });
  }
  localStorage.setItem(LS.pushEnabled, 'false');
  localStorage.removeItem(LS.lastSync);
}

export function dismissBanner() {
  ID.set('_dismissed');
}

export function notifyItemAdded({ dateKey, text, stream = null }) {
  const actorId = ID.get();
  if (!ID.isReal(actorId)) return; // not configured, skip silently
  if (localStorage.getItem(LS.pushEnabled) !== 'true') return; // disabled
  // fire-and-forget
  fetch('/.netlify/functions/notify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ actorId, dateKey, text, stream }),
    keepalive: true,
  }).catch(() => {});
}
